/* eslint-disable react-hooks/exhaustive-deps */
// src/pages/buyerNet/useBuyerNetActions.js
import { useCallback } from "react";
import {
  saveBuyerCalculator,
  updateBuyerCalculator,
} from "../services/buyerNetService";
import { buildBuyerPayload } from "../../../../utils/payloadBuilder";
import { generatePdf } from "../../PdfRouteService";
import { parseCurrency } from "../utils/buyerNet.shared";
import { withOriginationFeeConverted } from "../../../../utils/originationFee";

export const useBuyerNetActions = (
  openModal,
  closeModal,
  buyerformData,
  setFormData,
  companyId,
  showToaster,
  isQuickMode,
  maincompanyid,
  formDataRef,
  templateformData,
) => {
  // Save/Download/Print/Email are all invoked from inside ResultsModal via
  // index.js's renderModal(), whose element (and every closure baked into
  // it, including this hook's own handlers below) is cached by ModalContext
  // and NOT recreated on every BuyerNetSheet re-render — `buyerformData`
  // read directly would still be whatever it was the moment the modal was
  // first opened. `formDataRef.current` is read fresh inside a handler body
  // at call time, so it's always live regardless of which render's closure
  // actually gets invoked.
  //
  // Also converts the origination-fee row's percent to a dollar amount if
  // it's still raw — happens whenever ManualSheetSection never mounted this
  // session (e.g. the user reached this modal straight from the
  // address/Calculate flow), since that's the only other place this
  // conversion runs.
  const getEffectiveFormData = useCallback(() => {
    const currentFormData = formDataRef?.current ?? buyerformData;
    return {
      ...currentFormData,
      buyerClosingCostsMeta: withOriginationFeeConverted(
        currentFormData?.buyerClosingCostsMeta,
        templateformData?.conventional,
        currentFormData?.salesPrice,
      ),
    };
  }, [formDataRef, buyerformData, templateformData]);

  /**
   * Handle Calculate - Trigger Calculation & Open Results
   */
  const handleCalculate = useCallback(
    async (getTitleEscrowData, templateformData) => {
      if (
        !buyerformData?.salesPrice ||
        Number(buyerformData.salesPrice) === 0
      ) {
        openModal({
          title: "Warnings",
          size: "smallmodel",
          content: (
            <div className="modelspace application-fe">
              <p className="model-desc-details notice-icon">
                You haven&apos;t entered a Sales Price.
                <br />
                Do you want to continue without a Sales Price?
              </p>
              <div className="frm-btn popup-2-btn">
                <button
                  className="submit-btn btn-bg inpt-btn btn-discard"
                  onClick={() => closeModal()}
                >
                  Cancel
                </button>
                <button
                  className="submit-btn btn-bg inpt-btn"
                  onClick={() => {
                    closeModal();
                    runCalculation();
                  }}
                >
                  Continue
                </button>
              </div>
            </div>
          ),
        });

        return;
      }

      const runCalculation = () => {
        getTitleEscrowData(buyerformData, templateformData, "Conventional");
      };

      runCalculation();
    },
    [buyerformData, openModal, closeModal],
  );

  /**
   * Handle Save Buyer Calc
   */
  const handleSaveBuyerCalc = useCallback(async () => {
    try {
      const user = JSON.parse(localStorage.getItem("user"));
      const effectiveFormData = getEffectiveFormData();
      const buyerCalcId = effectiveFormData?.id;
      const payload = buildBuyerPayload({
        buyerformData: effectiveFormData,
        user,
        payload: {
          id: buyerCalcId,
          companyId: companyId,
          device: "web",
          calc: "Buyer",
          optionType: "save",
          mrsid: effectiveFormData?.addressMeta?.mrsid,
        },
      });

      if (!buyerCalcId) {
        const saveBuyerCalc = await saveBuyerCalculator(payload);
        if (saveBuyerCalc) {
          const savedId = saveBuyerCalc?.calculator?.id;
          if (savedId) {
            setFormData((prev) => ({ ...prev, id: savedId }));
          }
          showToaster({
            message: "Buyer Calculator saved successfully!",
            visible: true,
            type: "success",
          });
          closeModal();
        }
      } else {
        openModal({
          title: "Warnings",
          size: "mediumsizewidth",
          content: (
            <div className="modelspace application-fe">
              <p className="model-desc-details notice-icon">
                A saved file already exist with same file name, do you want to:
              </p>
              <div className="frm-btn popup-2-btn">
                <button
                  className="submit-btn btn-bg inpt-btn"
                  onClick={async () => {
                    await updateExistingCalc(payload);
                  }}
                >
                  Overwrite Existing
                </button>

                <button
                  className="submit-btn btn-bg inpt-btn"
                  onClick={async () => {
                    await saveNewCalc();
                  }}
                >
                  Save as New File
                </button>

                <button
                  className="submit-btn btn-bg inpt-btn btn-discard"
                  onClick={() => closeModal()}
                >
                  Cancel
                </button>
              </div>
            </div>
          ),
        });
      }
    } catch (error) {
      console.error("Error saving buyer calc:", error);
    }
  }, [
    getEffectiveFormData,
    companyId,
    setFormData,
    closeModal,
    openModal,
    showToaster,
  ]);

  const updateExistingCalc = async (payload) => {
    try {
      const buyerCalc = await updateBuyerCalculator(payload);
      if (buyerCalc) {
        closeModal();
        showToaster({
          message: "Buyer Calculator updated successfully!",
          visible: true,
          type: "success",
        });
      }
    } catch (error) {
      console.warn("Error while updating the file", error);
    }
  };

  const saveNewCalc = async () => {
    try {
      setFormData((prev) => ({ ...prev, id: null }));
      handleSaveBuyerCalc();
    } catch (error) {
      console.warn("Error while Saving the file", error);
    }
  };

  /**
   * Handle Download/Print PDF
   */
  const handleDownloadPrintPDF = useCallback(
    // The second parameter used to be the data to build the payload from —
    // now ignored (kept only so existing call sites don't need to change
    // their argument count) in favor of getEffectiveFormData(), which is
    // always live and origination-fee-converted regardless of what the
    // caller happened to have on hand.
    async (type, _ignoredFormData, calc = "buyer") => {
      try {
        const effectiveFormData = getEffectiveFormData();
        const salesPrice = parseCurrency(effectiveFormData?.salesPrice);
        if (!salesPrice || salesPrice === 0) {
          showToaster({
            message: "Failure! Please enter a Sales Price",
            visible: true,
            type: "error",
          });
          return;
        }

        const user = JSON.parse(localStorage.getItem("user"));
        const payload = buildBuyerPayload({
          buyerformData: effectiveFormData,
          user,
        });

        const data = await generatePdf({
          type: "buyer",
          calc,
          data: {
            ...payload,
            mrsid: effectiveFormData?.addressMeta?.mrsid,
            optionType:
              type === "download"
                ? "download"
                : type === "print"
                  ? "printdetailedestimate"
                  : "download",
            isQuickMode,
          },
        });

        const blob = new Blob([data], { type: "application/pdf" });
        const blobUrl = window.URL.createObjectURL(blob);

        if (type === "download") {
          const link = document.createElement("a");
          link.href = blobUrl;

          let preparedFor = (payload?.preparedFor || "New_Client")
            .replace(/\s+/g, "_")
            .replace(/[^a-zA-Z0-9_]/g, "");

          let timestamp = Math.floor(Date.now() / 1000);

          link.download = `Print_for_${preparedFor}_${timestamp}.pdf`;

          document.body.appendChild(link);
          link.click();
          link.remove();

          setTimeout(() => {
            window.URL.revokeObjectURL(blobUrl);
          }, 5000);
        }

        if (type === "print") {
          const printWindow = window.open("", "_blank", "width=900,height=700");

          if (printWindow) {
            printWindow.document.write(`
              <html>
                <head>
                  <title>Print PDF</title>
                </head>
                <body style="margin:0;">
                  <iframe id="printFrame"
                    src="${blobUrl}"
                    style="width:100%; height:100%; border:none;">
                  </iframe>
                  <script>
                    const frame = document.getElementById("printFrame");
                    frame.onload = function () {
                      setTimeout(() => {
                        frame.contentWindow.focus();
                        frame.contentWindow.print();
                      }, 300);
                    };
                  </script>
                </body>
              </html>
            `);

            printWindow.document.close();
          }
        }
      } catch (error) {
        console.warn("Error while downloading and print pdf", error);
      }
    },
    [showToaster, maincompanyid, getEffectiveFormData],
  );

  return {
    handleCalculate,
    handleSaveBuyerCalc,
    handleDownloadPrintPDF,
    getEffectiveFormData,
  };
};
